import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  CircularProgress,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

function UsersList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  const fetchUsers = async (pageNum = 0, limit = 10) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum + 1, // API uses 1-based pagination
        limit: limit,
      };

      const response = await api.contacts.getUsersList(params);

      if (response.data && response.data.users) {
        setUsers(response.data.users || []);
        setTotalUsers(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to fetch users',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchUsers(page, rowsPerPage);
  };

  const handleStatusToggle = async (userId, currentStatus, userName) => {
    const newStatus = currentStatus === 'Y' ? 'N' : 'Y';
    const actionText = newStatus === 'Y' ? 'activate' : 'deactivate';

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to ${actionText} user "${userName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${actionText}!`,
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await api.contacts.updateUserStatus(userId, newStatus);

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `User ${actionText}d successfully`,
          timer: 2000,
        });

        // Refresh the list
        fetchUsers(page, rowsPerPage);
      } catch (error) {
        console.error('Error updating user status:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || `Failed to ${actionText} user`,
        });
      }
    }
  };

  const handleOtpToggle = async (userId, currentStatus, userName) => {
    const newStatus = currentStatus === 'Y' ? 'N' : 'Y';
    const actionText = newStatus === 'Y' ? 'enable' : 'disable';

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to ${actionText} OTP login for user "${userName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${actionText}!`,
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await api.contacts.updateOtpStatus(userId, newStatus);

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `OTP login ${actionText}d successfully`,
          timer: 2000,
        });

        // Refresh the list
        fetchUsers(page, rowsPerPage);
      } catch (error) {
        console.error('Error updating OTP login status:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || `Failed to ${actionText} OTP login`,
        });
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="logo"
            borderRadius="lg"
            coloredShadow="logo"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Users List
            </MDTypography>
            <Box display="flex" gap={1}>
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} sx={{ color: 'white' }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <MDButton
                variant="contained"
                color="white"
                startIcon={<PersonAddIcon />}
                onClick={() => navigate('/user-registration')}
                sx={{ color: '#00798E' }}
              >
                Add New User
              </MDButton>
            </Box>
          </MDBox>

          <MDBox p={3}>

            {loading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </MDBox>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="caption" fontWeight="bold">
                            Name
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" fontWeight="bold">
                            Username
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" fontWeight="bold">
                            Email
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" fontWeight="bold">
                            Phone
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" fontWeight="bold">
                            User Type
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" fontWeight="bold">
                            Status
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            OTP Login
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" fontWeight="bold">
                            Last Login
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            Actions
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      {users.length > 0 ? (
                        users.map((userMap) => {
                          const contact = userMap.contact || {};
                          const user = userMap.user || {};
                          const phone = contact.phones && contact.phones[0];
                          const fullName = `${contact.first_name || ''} ${
                            contact.middle_name || ''
                          } ${contact.last_name || ''}`.trim();

                          return (
                            <TableRow key={userMap.id}>
                              <TableCell>
                                <MDTypography variant="caption">
                                  {fullName || 'N/A'}
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="caption">
                                  {user.user_name || 'N/A'}
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="caption">
                                  {contact.email_address || 'N/A'}
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="caption">
                                  {phone
                                    ? `${phone.country_code || ''} ${phone.phone_number || ''}`
                                    : 'N/A'}
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="caption">
                                  {user.user_type?.name || 'N/A'}
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={user.is_active === 'Y' ? 'Active' : 'Inactive'}
                                  color={user.is_active === 'Y' ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip
                                  title={
                                    user.is_otp_login_enabled === 'Y'
                                      ? 'Disable OTP Login'
                                      : 'Enable OTP Login'
                                  }
                                >
                                  <Switch
                                    checked={user.is_otp_login_enabled === 'Y'}
                                    onChange={() =>
                                      handleOtpToggle(
                                        userMap.irm_users_id,
                                        user.is_otp_login_enabled,
                                        fullName
                                      )
                                    }
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#4caf50',
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#4caf50',
                                      },
                                      '& .MuiSwitch-switchBase': {
                                        color: '#f44336',
                                      },
                                      '& .MuiSwitch-track': {
                                        backgroundColor: '#f44336',
                                      },
                                    }}
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="caption">
                                  {user.last_login
                                    ? dayjs(user.last_login).format('DD/MM/YYYY hh:mm A')
                                    : 'Never'}
                                </MDTypography>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip
                                  title={user.is_active === 'Y' ? 'Deactivate User' : 'Activate User'}
                                >
                                  <Switch
                                    checked={user.is_active === 'Y'}
                                    onChange={() =>
                                      handleStatusToggle(
                                        userMap.irm_users_id,
                                        user.is_active,
                                        fullName
                                      )
                                    }
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#4caf50',
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#4caf50',
                                      },
                                      '& .MuiSwitch-switchBase': {
                                        color: '#f44336',
                                      },
                                      '& .MuiSwitch-track': {
                                        backgroundColor: '#f44336',
                                      },
                                    }}
                                  />
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <MDTypography variant="caption" color="text">
                              No users found
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalUsers}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UsersList;
